import { useEffect, useRef, useState } from 'react';

/**
 * useScrollAnimation - Returns a ref and inView boolean.
 * Attach `ref` to any element to detect when it enters the viewport.
 * @param {number} threshold - 0 to 1, fraction of element visible to trigger
 * @param {string} rootMargin - offset from viewport edges
 * @param {boolean} triggerOnce - only trigger once (default: true)
 */
export function useScrollAnimation({ threshold = 0.01, rootMargin = '50px 0px 50px 0px', triggerOnce = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (triggerOnce) observer.unobserve(node);
        } else if (!triggerOnce) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, inView };
}

/**
 * useStaggeredAnimation - Returns a ref and animation state for staggered child animations.
 * @param {number} count - number of children
 * @param {number} staggerMs - delay between each child in ms
 */
export function useStaggeredAnimation(count, staggerMs = 80) {
  const { ref, inView } = useScrollAnimation();
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    if (!inView) return;
    setVisibleItems([]);
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        setVisibleItems(prev => [...prev, i]);
      }, i * staggerMs);
    }
  }, [inView, count, staggerMs]);

  return { ref, visibleItems, inView };
}
